import { create } from "zustand";
import type { Query } from "@tanstack/react-query";
import { normalizeAllowlist } from "@/lib/authz/fieldAllowlist";
import type { Capability } from "@/types/Capability";
import type { Filter } from "@/types/Filter";
import { queryClient } from "@/lib/api/queryClient";
import { readOnlySubset } from "@/lib/authz/capabilities";
import type { RoleSurfaceDefault } from "@/types/RoleSurfaceDefault";
import type { EventRoleTemplate } from "@/types/EventRoleTemplate";
import { rolePreviewScopeTemplateKey, roleTemplatesKey } from "@/lib/authz/roleTemplateQueryKeys";
import type { GuestReconcileCoarseDetail } from "@/lib/guest-reconcile/reconcileScheduler";
import {
  createPreviewToken,
  revokeEventRoleApiToken,
  type PreviewTokenResponse,
} from "@/lib/api/clients/EventRoleApiTokenClient";
import {
  endBlocks,
  usePreviewEndedStore,
  type PreviewEnded,
  type PreviewEndReason,
} from "./previewEndedStore";
import {
  isClientRejection,
  isDeterministicRefusal,
  isNoAnswerOrGateway,
  isRateLimited,
  retryAfterSeconds,
} from "./previewErrors";

/**
 * A role the admin can preview. For a template target the backend resolves
 * row/field scope live; for a base role it snapshots the read-only implicit
 * caps. `capabilities` here is the role's FULL capability set (chrome fidelity)
 * — the backend token is independently masked to read-only, and the axios
 * client blocks writes, so buttons appear exactly as the role sees them while
 * no mutation can land.
 */
export interface PreviewTarget {
  kind: "template" | "baseRole";
  /** Set when kind === "template". */
  templateId?: string;
  /** Set when kind === "baseRole" (VIEWER/EDITOR/ADMIN/OWNER). */
  baseRole?: string;
  /** Display name for the banner. */
  name: string;
  capabilities: Capability[];
  fieldAllowlist: string[] | null;
  rowFilters: Filter[];
  /**
   * The template's per-list default view/filter. Carried so a preview opens on
   * the list the PREVIEWED role starts on — without it the shell fell back to
   * the admin's own defaults and the setting was invisible in the one place
   * built to inspect a role.
   */
  surfaceDefaults?: RoleSurfaceDefault[];
  /**
   * B2: the template's optimistic-lock version as the row that built this
   * target carried it (`version ?? 0`) — the preview editor's `If-Match`.
   * Absent on a session persisted before B2 (and on base roles): the editor
   * then re-seeds from its fresh read of the template.
   */
  version?: number;
}

/**
 * B2: what the preview editor's PUT body was built from — the store's
 * `effective*` references at that moment. `markCapsSaved` adopts the server's
 * copy into `effective*` only while they are still these very references (the
 * grid stays enabled while saving: an edit made meanwhile must survive).
 */
export type PreviewSaveBasis = Pick<
  PreviewState,
  "effectiveCaps" | "effectiveRowFilters" | "effectiveRowFiltersWire" | "effectiveFieldAllowlist"
>;

export function saveBasisOf(s: PreviewSaveBasis): PreviewSaveBasis {
  return {
    effectiveCaps: s.effectiveCaps,
    effectiveRowFilters: s.effectiveRowFilters,
    effectiveRowFiltersWire: s.effectiveRowFiltersWire,
    effectiveFieldAllowlist: s.effectiveFieldAllowlist,
  };
}

export interface PreviewState {
  active: boolean;
  eventId: string | null;
  token: string | null;
  tokenId: string | null;
  expiresAt: number | null;
  target: PreviewTarget | null;
  /**
   * True only in the tab that MINTED the token (called startPreview). Tabs that
   * hydrate an in-flight session from sessionStorage (e.g. a builder opened via
   * window.open, which receives a copy of the opener's sessionStorage) are NOT
   * owners — they must never revoke the shared token on stop/expiry, or they'd
   * kill the preview still running in the main tab. The owner revokes; everyone
   * else just tears down local state and their own sessionStorage copy. Only
   * the owner names the token a re-mint/switch supersedes (B2-0), so a copy tab
   * never shortens the main tab's token. It flips to true in a copy tab after
   * its OWN re-mint swap or switch commit (it owns that token) — so it says
   * nothing about where the session started: see `startedHere`.
   */
  isOwner: boolean;
  /**
   * Audit r3 (security L1): this tab STARTED the session (a start from idle
   * committed here). False after hydration (a builder / copy tab opened from
   * the preview) and kept across this tab's own re-mint swaps and switch
   * commits. It decides what a local end of the session does (`wasOwner` of
   * `PreviewEnded`, `endBlocks`): only the starting tab's planned expiry is a
   * toast; a copy tab — also one that re-minted or switched itself — blocks and
   * leaves the request tombstone. `isOwner` stays for revoke / supersede only.
   */
  startedHere: boolean;
  /**
   * Targets the banner's role-switcher can jump to WITHOUT leaving preview.
   * Captured at start (while the admin still has full rights) so the switcher
   * never refetches templates mid-preview — the preview role may lack
   * `event-role-templates:read` and 403. Base roles are added by the banner
   * client-side.
   */
  availableTargets: PreviewTarget[];
  /**
   * ── Live capability editing ──
   * `baseCaps` is the previewed role's real (unedited) caps, snapshot at start —
   * the Reset target. `effectiveCaps` is the live, edited set that drives the
   * chrome (`EventLayoutShell` feeds it into `CapabilitiesProvider`). Editing is
   * INSTANT and client-only; only a change to the READ subset of a *template*
   * preview triggers a debounced token re-mint so newly-revealed read sections
   * load real data. `mintedReadCaps` is the read subset the CURRENT token was
   * minted with — the diff oracle that keeps write-only edits network-free.
   */
  baseCaps: Capability[];
  effectiveCaps: Capability[];
  /**
   * Row filters + field allowlist are the role's DATA SCOPE — enforced
   * server-side, so editing them only takes effect via a token re-mint that
   * carries the override (unlike caps, where write-cap chrome is client-only).
   * `base*` is the role's real scope (Reset target); `effective*` is the live
   * edited scope (drives chrome where applicable + the re-mint payload).
   */
  baseRowFilters: Filter[];
  effectiveRowFilters: Filter[];
  /**
   * The WIRE shape of `effectiveRowFilters` — what the template would actually
   * be saved with (arrays flattened, the "—" pick coerced to ∅/⊙, rows a
   * value-taking operator left empty removed; see `serializeRowFilters`).
   *
   * The editor has to keep the RAW rows for rendering — a row half-typed into
   * existence must not vanish from under the cursor — but the token must be
   * minted with what the role will really get. Minting the raw rows made the
   * preview show a DIFFERENT, and specifically a WIDER, scope than the save:
   * an empty `≠` row reaches Mongo as `$ne:""` and matches every guest without
   * that value, and the save drops it. Whoever writes raw rows also supplies
   * this; `null` means "the rows are already wire-shaped" (server-seeded or
   * hydrated state, which never passes through the editor).
   */
  effectiveRowFiltersWire: Filter[] | null;
  baseFieldAllowlist: string[] | null;
  effectiveFieldAllowlist: string[] | null;
  /** Read subset baked into the live token (internal; drives re-mint diffing). */
  mintedReadCaps: Capability[];
  /** Scope signature baked into the live token (internal; drives re-mint
   *  diffing). Built from the mint's INPUTS only — a response echo never
   *  reaches it, nor `effective*`, `base*` or `target` (B2-0 contract 7). */
  mintedScopeKey: string;
  /** True while a re-mint is in flight AND until its refetch has landed
   *  (banner/editor show "Updating data…"); capped at 20 s. */
  reminting: boolean;
  /** Set when the last re-mint failed (5xx, or a lost answer twice) — chrome
   *  is ahead of data; offer retry. */
  remintError: boolean;
  /** B2-0: the backend code of a REFUSED re-mint (4xx other than 429) — shown
   *  translated, no retry (the same body would be refused again); the next
   *  change mints normally. */
  remintErrorCode: string | null;
  /** Audit r3 (store LOW-2): a forced re-mint is OWED — a lost answer may have
   *  CAS-cut the live token to 60 s (mirrors the store's own debt for the
   *  chrome). During a 429 pause the paused line shows on it even when the
   *  token carries the edited state; an idle debt offers the (forced) retry. */
  remintDebt: boolean;
  /** B2-0: epoch ms until which re-mints are paused after a 429 (`Retry-After`,
   *  clamped 1…900 s, 60 s without a usable header). Edits only change the
   *  chrome meanwhile; the pause end mints the last state once. */
  remintPausedUntil: number | null;
  /** B2-0 (gate M3): an override mint came back WITHOUT `scopeOverride: true`
   *  (an older backend, mixed instances, a rollback) — the server cannot show
   *  row/field edits. Until stop or switch: no override is minted any more,
   *  cap edits still re-mint, and the token's rows/fields are the base ones. */
  scopeUnsupported: boolean;
  /** Bumped when the previewed role's SLOTTING scope (visible offerings/
   *  treatments/lanes) is saved live from the preview-bar editor. Unlike caps/
   *  row/field scope, slotting visibility is resolved server-side from the
   *  template at each request/WS-upgrade (not baked into the token), so the token
   *  needn't re-mint — but the open slotting WS must RECONNECT to re-read the new
   *  scope, and event-scoped REST caches must refetch. The board's WS effect
   *  depends on this nonce. */
  scopeRefreshNonce: number;
  /** Signal a live slotting-scope save: bump the nonce (→ WS reconnect) and
   *  refetch event-scoped REST so the board reflects the new scope instantly. */
  markScopeEdited: () => void;
  /** Replace the edited capability set (chrome updates synchronously). */
  setEffectiveCaps: (next: Set<Capability> | Capability[]) => void;
  /** Replace the edited row filters (template previews re-mint to apply).
   *  `wire` is the serialized shape the same rows would be SAVED with — pass it
   *  from any editor that holds raw, mid-edit rows (see
   *  {@link PreviewState.effectiveRowFiltersWire}). */
  setEffectiveRowFilters: (next: Filter[], wire?: Filter[] | null) => void;
  /** Replace the edited field allowlist (null = unrestricted). */
  setEffectiveFieldAllowlist: (next: string[] | null) => void;
  /** Restore caps + scope to the role's real values. */
  resetCaps: () => void;
  /** After persisting edits back to the template, adopt them as the new base
   *  (clears the "Modified" state without changing the live chrome/scope).
   *
   *  B2: with `saved.template` (the PUT's answer — the server's copy) the base
   *  becomes that copy; `effective*` follow it only if they are still what
   *  the body was built from (`saved.basis`). Either way the previewed target
   *  and its switcher twin are patched (content + `version`), the persisted
   *  session rewritten, the Roles list invalidated and the scope template's
   *  cache set to the copy. Without an argument (or an answer that is no
   *  template) the edited state is adopted as before. */
  markCapsSaved: (saved?: { template?: unknown; basis?: PreviewSaveBasis }) => void;
  /** B2: a fresh read of the previewed template while the editor is pristine
   *  (a newer version, or a session persisted before B2): base AND effective
   *  become the read, the target + switcher twin are patched, the session
   *  rewritten. */
  reseedFromTemplate: (template: EventRoleTemplate) => void;
  /** B2: a newer read whose content equals the base under edits — only the
   *  version moves (target + switcher twin + persisted session). */
  patchTargetVersion: (templateId: string, version: number) => void;
  /** Re-mint now if the token is behind the edited state — the EXPLICIT
   *  re-mint (the Retry button, the pause end, a failed switch); the edit
   *  debounce is internal (audit r3 MED-1). `forced` (internal): the one
   *  automatic re-mint after a lost answer — it mints even when nothing
   *  differs, because the server may already have shortened the live token to
   *  60 s. While such a re-mint is owed, every call is the forced one. */
  retryRemint: (opts?: { forced?: boolean }) => Promise<void>;
  /** Starts (or switches) a preview session for `eventId`. */
  startPreview: (
    eventId: string,
    target: PreviewTarget,
    availableTargets?: PreviewTarget[],
  ) => Promise<void>;
  /** Ends the session: revoke token (owner only) + restore the admin's view. */
  stopPreview: () => Promise<void>;
}

// ── Module state (one tab = one session) ──────────────────────────────────
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

// Re-mint debounce (live read-cap / scope editing): rapid edits coalesce into a
// single re-mint; a new edit cancels a pending one. Single-flight: a re-mint
// mid-air re-arms the timer instead of overlapping (overlapping mints would race
// on which token "wins" + leak revokes).
let remintTimer: ReturnType<typeof setTimeout> | null = null;
let remintInFlight = false;
/** The running re-mint (never rejects). Start/switch wait for it (B2-0 R3-F5). */
let remintPromise: Promise<void> | null = null;
const REMINT_DEBOUNCE_MS = 400;

/** B2-0 R3-F6: the one FORCED re-mint after a lost answer / 502–504. Its own
 *  timer — `remintTimer` is cleared by the reconcile's "nothing differs" branch,
 *  and an undo inside the window must not cancel it (the live token may already
 *  be CAS-shortened to 60 s). */
let autoRemintTimer: ReturnType<typeof setTimeout> | null = null;
/** R4 LOW-3: a forced re-mint is OWED until a successful swap, a switch commit
 *  or a stop settles it — a forced mint blocked by a running switch or a 429
 *  pause is kept, not dropped (a failed switch / the pause end honours it). */
let forcedRemintOwed = false;
const AUTO_REMINT_MS = 2000;
/** Every write of the debt goes through here: `remintDebt` mirrors it. */
function setForcedDebt(owed: boolean) {
  forcedRemintOwed = owed;
  if (usePreviewStore.getState().remintDebt !== owed) usePreviewStore.setState({ remintDebt: owed });
}
/** Audit r3 (store MED-1): the ONE forced re-mint queued or running — a second
 *  explicit call (the Retry button pressed again while it waits behind a
 *  flight) joins it instead of queueing another forced POST. */
let forcedRun: Promise<void> | null = null;
/** Audit r4 (store LOW-1): the forced re-mint whose POST is in the air (its
 *  debt was taken off at its start — `forcedTurn`). */
let forcedFlight: Promise<void> | null = null;
/** Audit r4 (store LOW-1): the ONE forced re-mint a Retry pressed during that
 *  flight asked for — it runs once the flight settled still owed; every press
 *  until then joins it (`retryAfterForcedFlight`). */
let forcedRetryAfter: Promise<void> | null = null;
/** Audit r3 (store MED-1): the desired-mint key the last forced re-mint sent
 *  without a swap (it failed, or a gate kept the old token). An IMPLICIT
 *  re-mint never sends it again on its own — the Retry button does. */
let failedForcedKey: string | null = null;

/** B2-0: the 429 pause timer (not `remintTimer`, which the reconcile clears). */
let pauseTimer: ReturnType<typeof setTimeout> | null = null;

/** The desired-mint key a 4xx refused — never re-sent until the edit changes. */
let rejectedMintKey: string | null = null;
/** Audit r2 (store LOW-3): the code that refusal showed — read only while the
 *  edited state IS `rejectedMintKey` (so it needs no clearing of its own). */
let rejectedMintCode: string | null = null;
/** Audit r5 (store LOW-1): `rejectedMintKey` is a DETERMINISTIC refusal (the
 *  400 family, `isDeterministicRefusal`) — only such a key outlives an undo to
 *  the token's state while a forced re-mint is owed. Set wherever the key is. */
let rejectedMintSticky = false;

/**
 * Audit r1 (BE contract LOW-4): the `remintErrorCode` of an every-read-cap-off
 * re-mint an OLDER backend answered (rollback, mixed instances — its answer
 * has no `scopeOverride` key). It reads `capabilities: []` as "the template's
 * caps", so that token would show the template's data under a bar that says
 * "no read caps". Not an `errors.*` key on purpose: the chrome falls back to
 * `rolePreview.remintRejected` ("the preview keeps showing the last state"),
 * which is exactly what happens — the old token stays.
 */
export const PREVIEW_CAPS_NOT_APPLIED = "previewCapsNotApplied";

/** R3-F1: bumped at every re-mint START. A bust switches the spinner off only
 *  when its generation is still the latest and no mint is in flight — the
 *  refetch of re-mint 1 must not end the spinner mid re-mint 2. */
let refreshGen = 0;
let remintingCapTimer: ReturnType<typeof setTimeout> | null = null;
/** `api` has no timeout; a hung refetch must not hold "Updating data…" forever. */
const REMINTING_CAP_MS = 20_000;

/**
 * B2-0 (L1 R1, L4, R3-F5, R3-F7, R4 LOW-2/4): start and switch run as ONE chain.
 *  - `switchChain`: each start/switch waits for the previous link (links never
 *    reject), then for a running re-mint — so a re-mint and a switch never
 *    overlap, and two switches never mint with the same predecessor.
 *  - `pendingSwitches` (entry +1, `finally` −1): while > 0 no re-mint, no
 *    pause-end mint and no forced mint goes out — an earlier link's `finally`
 *    cannot free a later one.
 *  - `startSeq`: drawn when a start/switch gets its TURN (and bumped by stop) —
 *    the await guard of start/switch only.
 *  - `sessionEpoch`: bumped only when a start/switch COMMITS and by stop — the
 *    session stamp the axios interceptor puts on every bearer request and the
 *    401 rule compares. A re-mint swap or a failed switch keeps the session.
 */
let switchChain: Promise<void> = Promise.resolve();
let pendingSwitches = 0;
let startSeq = 0;
let sessionEpoch = 0;

/**
 * B2-0 audit r1 (security F1): what a BLOCKING local end leaves behind (an
 * unplanned `endPreviewLocally("sessionExpired")` in any tab and, since audit
 * r2 F2, every local end of a copy tab — see `endBlocks`). Until a reload —
 * module state, gone with the page — the axios interceptor refuses every
 * request that names this event (and carries no bypass) client-side with the
 * same 401 `sessionExpired`, instead of letting it ride the admin cookie: a flow that
 * started under the preview and keeps going after a caught error (a per-file
 * upload loop, an await before its first write, a blur-commit outside the
 * outlet, a mount write of the subtree remounted as the admin) must never run
 * as the admin behind the notice. The token id only (never the secret).
 * Cleared only when a new session of the SAME event commits in this tab.
 */
export interface PreviewTombstone {
  eventId: string;
  tokenId: string | null;
}
let endedTombstone: PreviewTombstone | null = null;
/** Audit r2 (security F1): the notice record the tombstone's end raised — a
 *  refused request raises it again when nothing blocking is shown. */
let tombstoneEnded: PreviewEnded | null = null;

function clearRemintTimer() {
  if (remintTimer) {
    clearTimeout(remintTimer);
    remintTimer = null;
  }
}
function clearAutoRemintTimer() {
  if (autoRemintTimer) {
    clearTimeout(autoRemintTimer);
    autoRemintTimer = null;
  }
}
function clearPauseTimer() {
  if (pauseTimer) {
    clearTimeout(pauseTimer);
    pauseTimer = null;
  }
}
function clearRemintingCap() {
  if (remintingCapTimer) {
    clearTimeout(remintingCapTimer);
    remintingCapTimer = null;
  }
}

/** Order-insensitive equality for two capability lists. */
function sameCaps(a: readonly Capability[], b: readonly Capability[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  for (const c of b) if (!set.has(c)) return false;
  return true;
}

/** Canonical allowlist signature: a composite column's projection token is
 *  plumbing, not a scope change (see `normalizeAllowlist`). */
function fieldsKey(fieldAllowlist: string[] | null): string {
  const fa = normalizeAllowlist(fieldAllowlist);
  return JSON.stringify(fa == null ? null : [...fa].sort());
}

/** Stable signature of an override: `rowFilters: null` (template rows LIVE)
 *  and `[]` (no row restriction) are DIFFERENT scopes. */
function scopeKey(rowFilters: Filter[] | null, fieldAllowlist: string[] | null): string {
  const fa = normalizeAllowlist(fieldAllowlist);
  return JSON.stringify({
    rf: rowFilters,
    fa: fa == null ? null : [...fa].sort(),
  });
}

// ── Cross-tab handoff ──────────────────────────────────────────────────────
// The preview session is mirrored into sessionStorage so a builder opened in a
// NEW TAB via window.open (website/email/document/wallet/calendar builders all
// do this) inherits the read-only bearer instead of silently falling back to
// the admin's cookie auth. Per the HTML spec, window.open gives the new tab a
// COPY of the opener's sessionStorage at creation — so this propagates into the
// builder tab WITHOUT leaking into unrelated tabs the way localStorage would.
const SESSION_KEY = "veltric.preview.session";
// Don't hand a token to a new tab if it's within this window of expiry — the
// builder would start, then immediately get torn down.
const MIN_REMAINING_MS = 20_000;

interface PersistedSession {
  eventId: string;
  token: string;
  tokenId: string | null;
  expiresAt: number | null;
  target: PreviewTarget | null;
  availableTargets: PreviewTarget[];
  // Edited-cap state so a new-tab builder inherits the EDITED chrome (the token
  // it inherits already carries the edited read scope). Optional for back-compat
  // with sessions persisted before this field existed.
  baseCaps?: Capability[];
  effectiveCaps?: Capability[];
  mintedReadCaps?: Capability[];
  baseRowFilters?: Filter[];
  effectiveRowFilters?: Filter[];
  baseFieldAllowlist?: string[] | null;
  effectiveFieldAllowlist?: string[] | null;
  mintedScopeKey?: string;
}

function hasSessionStorage(): boolean {
  try {
    return typeof sessionStorage !== "undefined";
  } catch {
    return false;
  }
}

function writePersisted(s: PersistedSession): void {
  if (!hasSessionStorage()) return;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
  } catch {
    // Quota / privacy mode — preview still works in the originating tab via the
    // in-memory store; only the new-tab handoff is lost.
  }
}

function clearPersisted(): void {
  if (!hasSessionStorage()) return;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/** Read a still-valid persisted session, or null (also clears stale entries). */
function loadPersisted(): PersistedSession | null {
  if (!hasSessionStorage()) return null;
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as PersistedSession;
    if (!s || !s.eventId || !s.token) {
      clearPersisted();
      return null;
    }
    if (s.expiresAt != null && s.expiresAt - Date.now() <= MIN_REMAINING_MS) {
      clearPersisted();
      return null;
    }
    return s;
  } catch {
    clearPersisted();
    return null;
  }
}

/**
 * The handoff of the CURRENT session. A hydrating tab has no editor, so it
 * inherits what the token carries: the token's rows (`tokenRowFilters` — never
 * half-typed raw rows) and, while the server cannot apply an override
 * (`scopeUnsupported`), the base fields too — a copy tab gets no
 * `scopeNotApplied` notice and must not inherit an edit the token lacks (LOW-9).
 */
function persistedFrom(s: PreviewState): PersistedSession | null {
  if (!s.eventId || !s.token) return null;
  return {
    eventId: s.eventId,
    token: s.token,
    tokenId: s.tokenId,
    expiresAt: s.expiresAt,
    target: s.target,
    availableTargets: s.availableTargets,
    baseCaps: s.baseCaps,
    effectiveCaps: s.effectiveCaps,
    mintedReadCaps: s.mintedReadCaps,
    baseRowFilters: s.baseRowFilters,
    effectiveRowFilters: tokenRowFilters(s),
    baseFieldAllowlist: s.baseFieldAllowlist,
    effectiveFieldAllowlist: s.scopeUnsupported ? s.baseFieldAllowlist : s.effectiveFieldAllowlist,
    mintedScopeKey: s.mintedScopeKey,
  };
}

function isEventScoped(q: Query, eventId: string): boolean {
  return Array.isArray(q.queryKey) && q.queryKey.includes(eventId);
}

/** START / SWITCH / STOP: drop every event-scoped react-query entry so data
 *  refetches with/without the preview bearer. Company/auth keys (["user"…],
 *  ["company"…]) are untouched. There the shell remounts the page subtree
 *  (`outletKey`), and `remove` keeps the placeholder painted. */
function bustEventScopedCaches(eventId: string) {
  queryClient.removeQueries({ predicate: (q) => isEventScoped(q, eventId) });
}

/**
 * RE-MINT (and `markScopeEdited`): the same event, the same page, new bearer.
 * Nothing may blank or remount here (HIGH-1 R2: `resetQueries` emptied the
 * shell's own event query → spinner instead of the outlet → page, drawers and
 * the role editor remounted mid-word). Synchronous, in this order:
 *  1. a SILENT cancel of the active fetches (R3-M1): `cancelRefetch` only
 *     restarts queries that already HAVE data, so a first load still running
 *     with the old bearer would land as fresh and end the spinner on old data;
 *     silent + no revert = the path query-core takes for `cancelRefetch` itself
 *     (no intermediate `pending/idle` result);
 *  1b. a REVERTING cancel of observed but disabled queries with a fetch in
 *     flight (R4 LOW-1) — otherwise their old-bearer result lands un-invalidated
 *     and a re-enable within `staleTime` shows it;
 *  2. invalidate everything, refetch the active ones (data stays until then);
 *  3. remove observer-less entries (never shown again with the old scope);
 *  4. ONE `guestReconcileCoarse {eventId}` (R3-M2): providers that seeded state
 *     ONCE (the guest-list pager count, pages > 0, companions, registrants) are
 *     not reached by an invalidate.
 * Excluded: queries with `meta.previewScopeIndependent` (the shell's event and
 * role reads, the preview editor's catalogue and scope template) — they do not
 * depend on the bearer, and refetching them is what blanked the shell.
 * Resolves when the active refetches settled (errors included).
 */
function refreshEventScopedQueries(eventId: string): Promise<void> {
  const p = (q: Query) =>
    isEventScoped(q, eventId) && q.meta?.previewScopeIndependent !== true;
  void queryClient.cancelQueries(
    { predicate: (q) => p(q) && q.isActive() },
    { silent: true, revert: false },
  );
  void queryClient.cancelQueries(
    {
      predicate: (q) =>
        p(q) && !q.isActive() && q.getObserversCount() > 0 && q.state.fetchStatus !== "idle",
    },
    { revert: true },
  );
  const done = queryClient.invalidateQueries({ predicate: p, refetchType: "active" });
  queryClient.removeQueries({ predicate: (q) => p(q) && q.getObserversCount() === 0 });
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<GuestReconcileCoarseDetail>("guestReconcileCoarse", {
        detail: { eventId },
      }),
    );
  }
  return done.catch(() => {});
}

function clearExpiryTimer() {
  if (expiryTimer) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
}

/** Schedule a clean self-stop ~15s before the server-side expiry. */
function armExpiry(expiresAt: number | null) {
  clearExpiryTimer();
  if (expiresAt == null || Number.isNaN(expiresAt)) return;
  // Fire ~15s BEFORE the server-side expiry so we stop cleanly while the token
  // is still valid. Otherwise an in-flight request riding an expired bearer
  // would 401 → the response interceptor's refresh+retry re-attaches the dead
  // bearer → "auth-lost" could log the admin out.
  // B2-0: a LOCAL end — the main tab gets a toast, a builder/copy tab the
  // blocking notice (it silently became the admin otherwise).
  const ms = Math.max(0, expiresAt - Date.now() - 15_000);
  expiryTimer = setTimeout(() => endPreviewLocally("expired"), ms);
}

/**
 * The row filters a token may be minted with: the editor's wire shape when it
 * supplied one, otherwise the rows as they stand (already wire-shaped). Every
 * mint and every scope signature goes through this — the preview must never be
 * evaluated against rows the save would not send.
 */
function mintableRowFilters(s: {
  effectiveRowFilters: Filter[];
  effectiveRowFiltersWire: Filter[] | null;
}): Filter[] {
  return s.effectiveRowFiltersWire ?? s.effectiveRowFilters;
}

type ScopeInputs = Pick<
  PreviewState,
  | "effectiveCaps"
  | "effectiveRowFilters"
  | "effectiveRowFiltersWire"
  | "baseRowFilters"
  | "effectiveFieldAllowlist"
  | "baseFieldAllowlist"
  | "scopeUnsupported"
>;

/**
 * The bar's empty-wire guard: the save refuses to drop the role's LAST row
 * restriction (`rowFilterScopeWouldBeCleared`, the editor's `onSave`), so the
 * preview never shows that state either — it keeps the stored rows (B2-0
 * contract 5; hint `rolePreview.rowsKeptStored`).
 */
export function isRowGuardActive(s: ScopeInputs): boolean {
  return s.baseRowFilters.length > 0 && mintableRowFilters(s).length === 0;
}

/**
 * Did the admin change the ROWS? Decided RAW-vs-RAW (`baseRowFilters` is the
 * STORED shape, the wire is CANONICAL the moment the editor writes anything —
 * comparing the two made a legacy template, `tier` vs `additionalFields.tier`,
 * report a change for a net no-op edit and fabricate a scope override). Under
 * the empty-wire guard the rows count as unchanged. And a raw change whose WIRE
 * is exactly the stored rows (a half-added row the serializer drops) overrides
 * nothing: an override with the stored rows IS the template's scope.
 */
function rowsChanged(s: ScopeInputs): boolean {
  if (isRowGuardActive(s)) return false;
  if (JSON.stringify(s.effectiveRowFilters) === JSON.stringify(s.baseRowFilters)) return false;
  return JSON.stringify(mintableRowFilters(s)) !== JSON.stringify(s.baseRowFilters);
}

function fieldsChanged(s: ScopeInputs): boolean {
  return fieldsKey(s.effectiveFieldAllowlist) !== fieldsKey(s.baseFieldAllowlist);
}

/** Row or field edits that the token would need an override for. */
export function hasScopeEdits(s: ScopeInputs): boolean {
  return rowsChanged(s) || fieldsChanged(s);
}

interface MintPlan {
  readCaps: Capability[];
  override: boolean;
  /** Override only: the wire rows when the rows changed, else `null` (the
   *  template's rows LIVE — also under the empty-wire guard). */
  rowFilters: Filter[] | null;
  /** Override only: canonical, `null` = all fields. Always sent as a key. */
  fieldAllowlist: string[] | null;
  /** `""` without an override. */
  scopeKey: string;
  /** Read caps + scope, for "this exact body was refused". */
  key: string;
}

/**
 * B2-0: the ONE decision what the token should carry — used by the reconcile
 * (should a re-mint be scheduled?) AND by the re-mint itself (what to send).
 * They used to decide differently (`retryRemint` compared wire-vs-raw, so a cap
 * edit on a legacy template invented `scopeOverride: true`). Under
 * `scopeUnsupported` never an override.
 */
export function desiredMint(s: ScopeInputs): MintPlan {
  const readCaps = readOnlySubset(s.effectiveCaps);
  const rc = rowsChanged(s);
  const override = !s.scopeUnsupported && (rc || fieldsChanged(s));
  if (!override) {
    return {
      readCaps,
      override: false,
      rowFilters: null,
      fieldAllowlist: null,
      scopeKey: "",
      key: JSON.stringify([readCaps, ""]),
    };
  }
  const rowFilters = rc ? mintableRowFilters(s) : null;
  const fieldAllowlist = normalizeAllowlist(s.effectiveFieldAllowlist);
  const sk = scopeKey(rowFilters, fieldAllowlist);
  return {
    readCaps,
    override: true,
    rowFilters,
    fieldAllowlist,
    scopeKey: sk,
    key: JSON.stringify([readCaps, sk]),
  };
}

function isMinted(
  s: Pick<PreviewState, "mintedReadCaps" | "mintedScopeKey">,
  d: MintPlan,
): boolean {
  return sameCaps(d.readCaps, s.mintedReadCaps) && d.scopeKey === s.mintedScopeKey;
}

/** The data is behind the chrome: the token does not carry the edited state. */
export function isRemintOwed(s: PreviewState): boolean {
  if (!s.active || s.target?.kind !== "template") return false;
  return !isMinted(s, desiredMint(s));
}

/**
 * The row filters the chrome shows as "the role's rows" — what the TOKEN
 * applies: the start snapshot `baseRowFilters` while the rows are unchanged,
 * under the empty-wire guard and under `scopeUnsupported` (LOW-9), otherwise
 * the edited wire rows. Always by REFERENCE, never a fresh array: zustand 5 is
 * `useSyncExternalStore` without equality, a new array per call loops
 * ("getSnapshot should be cached") and crashes the shell (M4).
 */
export function tokenRowFilters(s: ScopeInputs): Filter[] {
  if (s.scopeUnsupported || !rowsChanged(s)) return s.baseRowFilters;
  return s.effectiveRowFiltersWire ?? s.effectiveRowFilters;
}

function revokeQuietly(eventId: string, tokenId: string) {
  // Bypass: issued while a session may be live — the revoke route refuses the
  // preview bearer. Best-effort: the token auto-expires (≤ 20 min, a CAS-
  // shortened predecessor ≤ 60 s).
  revokeEventRoleApiToken(eventId, tokenId, true).catch(() => {});
}

function armRemintingCap(gen: number) {
  clearRemintingCap();
  remintingCapTimer = setTimeout(() => {
    remintingCapTimer = null;
    if (gen === refreshGen) usePreviewStore.setState({ reminting: false });
  }, REMINTING_CAP_MS);
}

function beginReminting(): number {
  const gen = ++refreshGen;
  armRemintingCap(gen);
  usePreviewStore.setState({ reminting: true });
  return gen;
}

/**
 * Audit r1 (store LOW, J1): the 20-s cap counts PER PHASE. The mint's own cap
 * may already have switched "Updating data…" off when a slow mint lands; the
 * refetch with the new bearer then ran with no indicator — the chrome ahead of
 * the data. At the swap the spinner is back on, with a fresh cap, same gen.
 */
function remintingAtSwap(gen: number) {
  if (gen !== refreshGen) return;
  armRemintingCap(gen);
  if (!usePreviewStore.getState().reminting) usePreviewStore.setState({ reminting: true });
}

function endReminting(gen: number) {
  if (gen !== refreshGen || remintInFlight) return;
  clearRemintingCap();
  if (usePreviewStore.getState().reminting) usePreviewStore.setState({ reminting: false });
}

function startPause(seconds: number) {
  clearPauseTimer();
  const ms = seconds * 1000;
  pauseTimer = setTimeout(() => {
    pauseTimer = null;
    usePreviewStore.setState({ remintPausedUntil: null });
    // Exactly ONE automatic re-mint with the last state (forced if one is owed).
    void usePreviewStore
      .getState()
      .retryRemint(forcedRemintOwed ? { forced: true } : undefined);
  }, ms);
  usePreviewStore.setState({ remintPausedUntil: Date.now() + ms });
}

function armAutoRemint() {
  clearAutoRemintTimer();
  autoRemintTimer = setTimeout(() => {
    autoRemintTimer = null;
    setForcedDebt(true);
    void usePreviewStore.getState().retryRemint({ forced: true });
  }, AUTO_REMINT_MS);
}

/**
 * Decide whether a live edit needs a token re-mint and (debounced) trigger it.
 * Only template previews re-mint — base-role previews are chrome-only (no
 * server-side row/field scope; their reads already match the role). A re-mint is
 * needed when the READ-cap subset OR the data scope the token was minted with
 * no longer matches the edited state ({@link desiredMint}); write-only cap
 * edits and toggled-then-undone edits never trip it. During a 429 pause it arms
 * nothing (the pause end mints the last state), and a body a 4xx refused is not
 * sent again until the edit changes.
 */
function scheduleReconcile() {
  const s = usePreviewStore.getState();
  if (!s.active || !s.target || s.target.kind !== "template") return;
  const d = desiredMint(s);
  if (isMinted(s, d)) {
    clearRemintTimer();
    // Audit r2 (store LOW-1): not while a forced re-mint is still owed — the
    // live token may be CAS-cut to 60 s; its error stays (Retry is forced).
    // Audit r3 (store LOW-1): `showDebt` shows a refusal only while the edited
    // state IS the refused body, the retry otherwise. Audit r4 (store LOW-2):
    // a DETERMINISTIC refused key is kept here — an undo to the token's state
    // dropped it, and a redo of the refused body then offered a Retry that
    // re-sent it (its in-flight twin showed the refusal). Audit r5 (store
    // LOW-1): a transient one (409 `hashFailed`, 401 / 403 / 404, the caps
    // gate) goes, as before — kept, it stuck across the undo and every later
    // edit, and back on that body nothing paid the debt.
    if (forcedRemintOwed) {
      if (rejectedMintKey !== d.key && !rejectedMintSticky) rejectedMintKey = null;
      showDebt();
      return;
    }
    rejectedMintKey = null;
    if (s.remintError || s.remintErrorCode) {
      usePreviewStore.setState({ remintError: false, remintErrorCode: null });
    }
    return;
  }
  clearRemintTimer();
  if (s.remintPausedUntil != null) return;
  if (d.key === rejectedMintKey) {
    showRefusalIfBack();
    return;
  }
  remintTimer = setTimeout(() => {
    remintTimer = null;
    void implicitRemint();
  }, REMINT_DEBOUNCE_MS);
}

/** Re-arm the one debounce timer (a mint is mid-air): the latest edit lands
 *  once it settled — one pending caller, never a queue. */
function rearmRemint() {
  clearRemintTimer();
  remintTimer = setTimeout(() => {
    remintTimer = null;
    void implicitRemint();
  }, REMINT_DEBOUNCE_MS);
}

/**
 * Audit r3 (store MED-1): an IMPLICIT re-mint — the debounce of an edit or its
 * re-arm while a mint flies. Without a debt it is the plain re-mint. While a
 * forced re-mint is OWED it pays the debt only with an edit the token does not
 * carry yet, and only when nothing else is on the way: a mint in flight or a
 * forced one waiting ⇒ re-arm the one timer (before, every call became its own
 * forced waiter and one failure fanned out into one forced POST per edit made
 * during the flight); the body a 4xx refused or the last forced mint already
 * sent ⇒ nothing (the footer offers its refusal / the forced Retry). A start or
 * switch, a 429 pause, the Retry button, the pause end and a failed switch
 * honour the debt themselves.
 */
function implicitRemint(): Promise<void> {
  if (!forcedRemintOwed) return plainRemint();
  const st = usePreviewStore.getState();
  if (!st.active || !st.eventId || !st.target || st.target.kind !== "template") {
    return Promise.resolve();
  }
  if (pendingSwitches > 0 || st.remintPausedUntil != null) return Promise.resolve();
  if (remintInFlight || remintPromise || forcedRun) {
    rearmRemint();
    return Promise.resolve();
  }
  const d = desiredMint(st);
  if (d.key === rejectedMintKey) {
    showRefusalIfBack();
    return Promise.resolve();
  }
  if (isMinted(st, d) || d.key === failedForcedKey) {
    showDebt();
    return Promise.resolve();
  }
  return runForcedRemint();
}

/** The plain (non-forced) re-mint: the debounce without a debt, and the Retry
 *  button without one. */
async function plainRemint(): Promise<void> {
  const st = usePreviewStore.getState();
  if (!st.active || !st.eventId || !st.target || st.target.kind !== "template") return;
  // A start/switch holds the chain: it re-seeds on success, and its failure
  // re-reconciles — a re-mint now would race it for the same predecessor.
  if (pendingSwitches > 0) return;
  // 429 pause: only the pause end mints (once, with the last state).
  if (st.remintPausedUntil != null) return;
  if (remintInFlight) {
    // A mint is mid-air; re-arm so the latest edit lands once it settles.
    rearmRemint();
    return;
  }
  await trackRemint(false);
}

/**
 * Audit r2 (store LOW-1): a FORCED re-mint exists because a lost mint may have
 * CAS-cut the live token to 60 s — "nothing differs" is no answer to that.
 * When it ends without a swap (an error, a gate), the debt stays OWED: the
 * pause end, a failed switch and the Retry button honour it, and its error is
 * never cleared as "already minted". Not after a stop (the session is gone).
 */
function keepForcedDebt(forced: boolean, d: MintPlan) {
  if (forced && usePreviewStore.getState().active) {
    setForcedDebt(true);
    failedForcedKey = d.key;
  }
}

function onRemintError(e: unknown, d: MintPlan, forced: boolean, gen: number) {
  const store = usePreviewStore;
  keepForcedDebt(forced, d);
  if (isRateLimited(e)) {
    // 429: the server restored the predecessor's expiry; pause, then ONE mint.
    // Audit r1 (tests-ux F4): an earlier refusal is stale now — the pause and
    // an old "refused" together read as two problems; and its key goes too, so
    // going back to that edit is sent (and refused, and shown) at the pause end.
    rejectedMintKey = null;
    store.setState({ remintError: false, remintErrorCode: null });
    startPause(retryAfterSeconds(e));
  } else if (isClientRejection(e)) {
    // 4xx: this body is refused — a retry would send it again, endlessly.
    rejectedMintKey = d.key;
    rejectedMintSticky = isDeterministicRefusal(e);
    const code = e instanceof Error && e.message ? e.message : "unknown";
    rejectedMintCode = code;
    store.setState({ remintError: false, remintErrorCode: code });
    clearErrorIfMinted();
  } else if (!forced && isNoAnswerOrGateway(e)) {
    // Unknown whether the server minted: after a successful CAS the live token
    // dies in 60 s. One forced re-mint with the then-current state and token —
    // unless one already waits behind this mint (audit r3 MED-1): it runs next,
    // with the then-current state and token; a second one would only repeat it.
    store.setState({ remintError: false });
    if (!forcedRun) armAutoRemint();
  } else {
    // Keep chrome edits + the OLD token (data reflects the old scope/caps).
    // Flag it so the banner can surface a retry — never silently diverge.
    store.setState({ remintError: true, remintErrorCode: null });
    // Audit r3: the forced re-send of a refused body answered no refusal this
    // time — its Retry (forced) is what the footer offers now (K3).
    if (forced && rejectedMintKey === d.key) rejectedMintKey = null;
    clearErrorIfMinted();
    if (!forced) showRefusalIfBack();
  }
  if (forced) showDebt();
  endReminting(gen);
}

/**
 * Audit r3 (store LOW-1 / LOW-2): what the bar shows while a forced re-mint is
 * OWED and no mint or switch is on the way (a 429 pause shows itself, through
 * `remintDebt`): a refusal only while the edited state IS the refused body;
 * otherwise the plain retry, whose Retry is the forced re-mint that pays the
 * debt. Before, the forced B refused (400) and an undo to A kept "B refused"
 * on screen for A — under a code no Retry —, and a forced override the scope
 * gate kept showed nothing at all, while the live token might die in 60 s.
 */
function showDebt() {
  if (!forcedRemintOwed || remintInFlight || pendingSwitches > 0) return;
  const s = usePreviewStore.getState();
  if (!s.active || s.remintPausedUntil != null) return;
  if (refusalShown(s)) {
    if (s.remintError || s.remintErrorCode !== rejectedMintCode) {
      usePreviewStore.setState({ remintError: false, remintErrorCode: rejectedMintCode });
    }
    return;
  }
  // Audit r3 (store LOW-2): also when nothing is shown at all (a forced
  // override the scope gate kept, X3) — the debt is never silent.
  if (!s.remintError || s.remintErrorCode != null) {
    usePreviewStore.setState({ remintError: true, remintErrorCode: null });
  }
}

/**
 * Audit r2 (store LOW-3): the edited state is a body a 4xx refused (the admin
 * went back to it, e.g. B refused → C failed with a 500 → back to B). Its
 * refusal is what the footer shows — not the other mint's "retry", whose
 * Retry would send nothing (a refused body is not re-sent until the edit
 * changes).
 */
function showRefusalIfBack() {
  const now = usePreviewStore.getState();
  if (rejectedMintKey == null || desiredMint(now).key !== rejectedMintKey) return;
  if (now.remintError || now.remintErrorCode !== rejectedMintCode) {
    usePreviewStore.setState({ remintError: false, remintErrorCode: rejectedMintCode });
  }
}

/**
 * Audit r1 (store LOW, the error twin of the MED below): the edit that failed
 * was taken back while its mint flew — the token (unchanged) already carries
 * the current state. "Couldn't update — retry" / "refused" would name an edit
 * that no longer exists. Cleared here, NOT via `scheduleReconcile()`: that
 * would auto-retry a 500. The refused key goes too — redoing that edit is a
 * new user action and is sent (and refused, and shown) again.
 */
function clearErrorIfMinted() {
  // A forced re-mint still owed (audit r2 LOW-1): the token may die in 60 s.
  if (forcedRemintOwed) return;
  const now = usePreviewStore.getState();
  if (!isMinted(now, desiredMint(now))) return;
  rejectedMintKey = null;
  usePreviewStore.setState({ remintError: false, remintErrorCode: null });
}

async function remintOnce(forced: boolean): Promise<void> {
  const store = usePreviewStore;
  const st = store.getState();
  if (!st.active || !st.eventId || !st.target) return;
  // Audit r2 (security F4): a BASE-ROLE session never re-mints for an edit (its
  // chrome is client-only), but it owes the FORCED re-mint as much as a
  // template session: a switch whose answer was lost (or came after the 30-s
  // timeout) may have CAS-shortened its token to 60 s. The same base role is
  // minted once more, naming that token; no scope, no caps, no gates.
  const baseRole =
    forced && st.target.kind === "baseRole" && st.target.baseRole ? st.target.baseRole : undefined;
  if (st.target.kind !== "template" && !baseRole) return;
  const d = desiredMint(st);
  if (!forced) {
    if (isMinted(st, d)) {
      if (st.remintError || st.remintErrorCode) {
        store.setState({ remintError: false, remintErrorCode: null });
      }
      return;
    }
    if (d.key === rejectedMintKey) return;
  }
  const { eventId, target, expiresAt, tokenId, isOwner } = st;
  remintInFlight = true;
  const gen = beginReminting();
  let res: PreviewTokenResponse;
  try {
    // Under the admin cookie (bypass): the mint route refuses the preview
    // bearer (`EVENT-CUSTOM`) — REMINT 403'd every live edit before B2-0.
    // `templateId` keeps the role's scope unless overridden; the current expiry
    // PRESERVES the countdown (the backend clamps to ≤ a fresh TTL). Only the
    // minting tab names its predecessor (a copy tab mints in the fresh bucket
    // and never shortens the main tab's token).
    const keepExpiry = expiresAt != null ? new Date(expiresAt).toISOString() : undefined;
    const supersedes = isOwner && tokenId ? { supersedesTokenId: tokenId } : {};
    res = await createPreviewToken(
      eventId,
      baseRole
        ? { baseRole, expiresAt: keepExpiry, ...supersedes }
        : {
            templateId: target.templateId,
            capabilities: d.readCaps,
            scopeOverride: d.override,
            // With an override BOTH keys go out, `null` included (it survives JSON).
            ...(d.override ? { rowFilters: d.rowFilters, fieldAllowlist: d.fieldAllowlist } : {}),
            expiresAt: keepExpiry,
            ...supersedes,
          },
      true,
    );
  } catch (e) {
    remintInFlight = false;
    onRemintError(e, d, forced, gen);
    return;
  }
  // Stale-remint guard: the session changed identity while this mint was in
  // flight (a stop landed — a switch can no longer overlap, it waits for this
  // mint). Applying it would clobber the current state with a stale scope and
  // orphan its token: revoke OUR fresh token — always, also in a copy tab (L7);
  // `isOwner` only ever decides about the predecessor.
  const current = store.getState();
  if (!current.active || current.tokenId !== tokenId) {
    remintInFlight = false;
    revokeQuietly(eventId, res.eventRoleApiTokenId);
    endReminting(gen);
    return;
  }
  // Gate (M3): an override the server did not apply (no `scopeOverride: true`
  // — an older backend, mixed instances, a rollback) counts as FAILED. Keep the
  // old token and `mintedScopeKey`, revoke the new one, stop asking for
  // overrides until stop/switch, and re-reconcile once so a cap edit that rode
  // along goes out caps-only now (LOW-8).
  if (!baseRole && d.override && res.scopeOverride !== true) {
    remintInFlight = false;
    revokeQuietly(eventId, res.eventRoleApiTokenId);
    keepForcedDebt(forced, d);
    store.setState({ scopeUnsupported: true });
    endReminting(gen);
    scheduleReconcile();
    return;
  }
  // Audit r1 (BE contract LOW-4): the same window for an emptied cap list. An
  // answer WITHOUT the `scopeOverride` key is an older backend, which minted
  // `capabilities: []` as the TEMPLATE's caps. Treated as refused: the new
  // token is revoked, the old one (the last minted caps) kept, the refusal
  // shown, this body not re-sent until the edit changes. Bounded by the
  // template either way; this keeps the bar honest.
  if (!baseRole && !d.override && d.readCaps.length === 0 && typeof res.scopeOverride !== "boolean") {
    remintInFlight = false;
    revokeQuietly(eventId, res.eventRoleApiTokenId);
    keepForcedDebt(forced, d);
    rejectedMintKey = d.key;
    // Mixed instances: the next answer may come from a new backend.
    rejectedMintSticky = false;
    rejectedMintCode = PREVIEW_CAPS_NOT_APPLIED;
    store.setState({ remintError: false, remintErrorCode: PREVIEW_CAPS_NOT_APPLIED });
    clearErrorIfMinted();
    if (forced) showDebt();
    endReminting(gen);
    return;
  }
  const newExpiry = Date.parse(res.expiresAt);
  const normExpiry = Number.isNaN(newExpiry) ? expiresAt : newExpiry;
  // Swap to the new bearer FIRST (no auth gap), then revoke the old one. The
  // swap settles any owed forced re-mint: the predecessor is replaced.
  setForcedDebt(false);
  failedForcedKey = null;
  clearAutoRemintTimer();
  rejectedMintKey = null;
  store.setState({
    token: res.token,
    tokenId: res.eventRoleApiTokenId,
    expiresAt: normExpiry,
    // A base-role token carries the role's own read caps, never an edit.
    ...(baseRole ? {} : { mintedReadCaps: d.readCaps, mintedScopeKey: d.scopeKey }),
    isOwner: true,
    remintError: false,
    remintErrorCode: null,
  });
  remintInFlight = false;
  const persisted = persistedFrom(store.getState());
  if (persisted) writePersisted(persisted);
  // "Updating data…" stays on until the refetch with the new bearer landed.
  remintingAtSwap(gen);
  void refreshEventScopedQueries(eventId).then(() => endReminting(gen));
  armExpiry(normExpiry);
  if (isOwner && tokenId) revokeQuietly(eventId, tokenId);
  // Audit r1 (store MED): the token now carries `d`, the plan taken at mint
  // START. An edit made while it flew that went back to the OLD token's state
  // (undo, Reset, a backspace) found "already minted" in the reconcile and
  // armed nothing — the token kept the in-between state for good, the chrome
  // the reverted one, no badge. Re-reconcile against the new token: exactly
  // one more mint when the edited state differs, nothing otherwise.
  scheduleReconcile();
}

/** Runs one re-mint and publishes it as `remintPromise` (never rejects). */
function trackRemint(forced: boolean): Promise<void> {
  const p = remintOnce(forced).catch(() => {});
  remintPromise = p;
  void p.finally(() => {
    if (remintPromise === p) remintPromise = null;
  });
  return p;
}

/**
 * The forced re-mint (R3-F6, R4 LOW-3). It waits ONLY for a running re-mint —
 * never for the switch chain (a switch at its turn waits for `remintPromise`;
 * waiting for each other would deadlock) — and re-checks after the wait: a
 * successful swap, a switch commit or a stop settled it; a running switch or a
 * 429 pause keeps it OWED (honoured by the failed switch / the pause end).
 * Audit r3 (store MED-1): ONE at a time — a call while one is queued or
 * running joins it (`forcedRun`).
 */
function runForcedRemint(): Promise<void> {
  if (forcedRun) return forcedRun;
  const p: Promise<void> = forcedTurn().finally(() => {
    if (forcedRun === p) forcedRun = null;
  });
  forcedRun = p;
  return p;
}

async function forcedTurn(): Promise<void> {
  while (remintPromise) await remintPromise;
  if (!forcedRemintOwed) return;
  const st = usePreviewStore.getState();
  // Template AND base-role sessions (audit r2 F4) owe it.
  if (!st.active || !st.eventId || !st.target) {
    setForcedDebt(false);
    return;
  }
  if (pendingSwitches > 0) return;
  if (st.remintPausedUntil != null) return;
  setForcedDebt(false);
  const flight = trackRemint(true); // never rejects
  forcedFlight = flight;
  await flight;
  if (forcedFlight === flight) forcedFlight = null;
}

/** The bar shows the refusal of the edited state itself (no Retry there). */
function refusalShown(s: PreviewState): boolean {
  return rejectedMintKey != null && s.target?.kind === "template" && desiredMint(s).key === rejectedMintKey;
}

/**
 * Audit r4 (store LOW-1): the Retry button pressed while the FORCED re-mint is
 * in the air — reachable once the flight outlasts the 20-s spinner cap and the
 * Retry shows again. Its debt was taken off at the flight's start, so the
 * press found nothing owed and took the plain path — whose re-armed debounce
 * then met the flight's body as `failedForcedKey` and sent nothing: the press
 * was lost while the live token might die ~60 s after the lost answer. Now the
 * press joins the flight; once it settled still owed, in the same session, and
 * the bar offers the Retry (not the refusal of the edited state), exactly ONE
 * more forced re-mint runs — every further press until then joins it, so five
 * presses are one POST, never a burst.
 */
function retryAfterForcedFlight(run: Promise<void>): Promise<void> {
  if (forcedRetryAfter) return forcedRetryAfter;
  const epoch = sessionEpoch;
  const p: Promise<void> = run.then(() => {
    if (forcedRetryAfter === p) forcedRetryAfter = null;
    if (!forcedRemintOwed || sessionEpoch !== epoch) return;
    if (refusalShown(usePreviewStore.getState())) return;
    return runForcedRemint();
  });
  forcedRetryAfter = p;
  return p;
}

/** A failed switch: the RUNNING session stays (same `sessionEpoch`). */
function afterFailedSwitch(noAnswer: boolean) {
  const s = usePreviewStore.getState();
  if (!s.active) return;
  // A template OR a base-role session (audit r2 F4: a base-role switch
  // answered after the 30-s timeout had cut the running token to 60 s, and
  // nothing re-minted it — the session died a minute later, unplanned).
  if (forcedRemintOwed) {
    void usePreviewStore.getState().retryRemint({ forced: true });
  } else if (noAnswer) {
    // The switch's CAS may have shortened the live token: one forced
    // re-mint of the running session (old role, current state).
    armAutoRemint();
  }
  // reconcileOnSwitchFail: an edit made while the switch held the chain mints.
  scheduleReconcile();
}

type SwitchTurn = { error?: unknown; failedSwitch?: { noAnswer: boolean } };

// Hydrate from a sessionStorage handoff (new-tab builder) BEFORE the store is
// created so the very first axios request in that tab already carries the bearer.
/** The PUT / GET answer is the server's copy of THIS template (not a stub). */
function savedTemplateOf(v: unknown, templateId: string | undefined): EventRoleTemplate | null {
  if (!v || typeof v !== "object" || !templateId) return null;
  const t = v as Partial<EventRoleTemplate>;
  if (t.eventRoleTemplateId !== templateId || !Array.isArray(t.capabilities)) return null;
  return v as EventRoleTemplate;
}

/** The target fields a server copy decides (name stays: it is the label the row built). */
function targetPatch(t: PreviewTarget, tmpl: EventRoleTemplate): PreviewTarget {
  return {
    ...t,
    capabilities: tmpl.capabilities ?? t.capabilities,
    rowFilters: tmpl.rowFilters ?? [],
    fieldAllowlist: tmpl.fieldAllowlist ?? null,
    surfaceDefaults: tmpl.surfaceDefaults ?? t.surfaceDefaults,
    version: tmpl.version ?? 0,
  };
}

/** `target` + its switcher twin, patched for one template. */
function patchedTargets(
  s: Pick<PreviewState, "target" | "availableTargets">,
  templateId: string,
  patch: (t: PreviewTarget) => PreviewTarget,
): Pick<PreviewState, "target" | "availableTargets"> {
  const hit = (t: PreviewTarget | null) => !!t && t.kind === "template" && t.templateId === templateId;
  return {
    target: s.target && hit(s.target) ? patch(s.target) : s.target,
    availableTargets: s.availableTargets.map((t) => (hit(t) ? patch(t) : t)),
  };
}

const hydrated = loadPersisted();

const IDLE_SESSION = {
  active: false,
  eventId: null,
  token: null,
  tokenId: null,
  expiresAt: null,
  target: null,
  isOwner: false,
  startedHere: false,
  availableTargets: [] as PreviewTarget[],
  baseCaps: [] as Capability[],
  effectiveCaps: [] as Capability[],
  mintedReadCaps: [] as Capability[],
  baseRowFilters: [] as Filter[],
  effectiveRowFilters: [] as Filter[],
  effectiveRowFiltersWire: null,
  baseFieldAllowlist: null,
  effectiveFieldAllowlist: null,
  mintedScopeKey: "",
  reminting: false,
  remintError: false,
  remintErrorCode: null,
  remintDebt: false,
  remintPausedUntil: null,
  scopeUnsupported: false,
} satisfies Partial<PreviewState>;

export const usePreviewStore = create<PreviewState>((set, get) => ({
  active: !!hydrated,
  eventId: hydrated?.eventId ?? null,
  token: hydrated?.token ?? null,
  tokenId: hydrated?.tokenId ?? null,
  expiresAt: hydrated?.expiresAt ?? null,
  target: hydrated?.target ?? null,
  // A hydrated tab did NOT mint the token → it is a non-owner and must not
  // revoke on stop/expiry — nor did it start the session (audit r3 L1).
  isOwner: false,
  startedHere: false,
  availableTargets: hydrated?.availableTargets ?? [],
  baseCaps: hydrated?.baseCaps ?? hydrated?.target?.capabilities ?? [],
  effectiveCaps:
    hydrated?.effectiveCaps ?? hydrated?.target?.capabilities ?? [],
  mintedReadCaps:
    hydrated?.mintedReadCaps ??
    readOnlySubset(hydrated?.target?.capabilities ?? []),
  baseRowFilters: hydrated?.baseRowFilters ?? hydrated?.target?.rowFilters ?? [],
  effectiveRowFilters:
    hydrated?.effectiveRowFilters ?? hydrated?.target?.rowFilters ?? [],
  // Hydrated state was minted from, and persisted by, another tab — already
  // wire-shaped. Only a live editor produces raw rows.
  effectiveRowFiltersWire: null,
  baseFieldAllowlist:
    hydrated?.baseFieldAllowlist ?? hydrated?.target?.fieldAllowlist ?? null,
  effectiveFieldAllowlist:
    hydrated?.effectiveFieldAllowlist ?? hydrated?.target?.fieldAllowlist ?? null,
  mintedScopeKey: hydrated?.mintedScopeKey ?? "",
  reminting: false,
  remintError: false,
  remintErrorCode: null,
  remintDebt: false,
  remintPausedUntil: null,
  scopeUnsupported: false,
  scopeRefreshNonce: 0,

  setEffectiveCaps: (next) => {
    // Chrome updates synchronously (re-renders every can() consumer). The
    // reconcile below only matters for template previews + the read subset.
    set({ effectiveCaps: Array.from(next) });
    scheduleReconcile();
  },

  setEffectiveRowFilters: (next, wire) => {
    // Row filters are server-enforced — no instant chrome; reconcile re-mints.
    // `next` is what the editor RENDERS, `wire` what it would SAVE; the mint
    // follows `wire` so the preview can never show a scope the role won't get.
    set({ effectiveRowFilters: next, effectiveRowFiltersWire: wire ?? null });
    scheduleReconcile();
  },

  setEffectiveFieldAllowlist: (next) => {
    // Field allowlist drives column visibility (instant chrome) AND server-side
    // masking (needs a re-mint) — do both.
    set({ effectiveFieldAllowlist: next });
    scheduleReconcile();
  },

  resetCaps: () => {
    set({
      effectiveCaps: get().baseCaps,
      effectiveRowFilters: get().baseRowFilters,
      // Back to the role's real scope, which is already wire-shaped.
      effectiveRowFiltersWire: null,
      effectiveFieldAllowlist: get().baseFieldAllowlist,
    });
    scheduleReconcile();
  },

  markCapsSaved: (saved) => {
    const s = get();
    const templateId = s.target?.kind === "template" ? s.target.templateId : undefined;
    const tmpl = savedTemplateOf(saved?.template, templateId);
    if (!tmpl || !templateId) {
      // Adopt what was SAVED, on both sides. Keeping the raw rows here would
      // leave the editor showing rows the server never received while the
      // "Modified" badge reads clean — the same lie the template editor grew.
      const rows = mintableRowFilters(s);
      set({
        baseCaps: s.effectiveCaps,
        baseRowFilters: rows,
        effectiveRowFilters: rows,
        effectiveRowFiltersWire: null,
        baseFieldAllowlist: s.effectiveFieldAllowlist,
        ...(templateId
          ? patchedTargets(s, templateId, (t) => ({
              ...t,
              capabilities: s.effectiveCaps,
              rowFilters: rows,
              fieldAllowlist: s.effectiveFieldAllowlist,
            }))
          : {}),
      });
    } else {
      // B2: the server's copy is the base (it drops orphans, normalises);
      // the edited state follows it only if nobody edited since the body.
      const b = saved?.basis;
      const unchanged =
        !b ||
        (s.effectiveCaps === b.effectiveCaps &&
          s.effectiveRowFilters === b.effectiveRowFilters &&
          s.effectiveRowFiltersWire === b.effectiveRowFiltersWire &&
          s.effectiveFieldAllowlist === b.effectiveFieldAllowlist);
      const caps = tmpl.capabilities ?? s.effectiveCaps;
      const rows = tmpl.rowFilters ?? [];
      const fields = tmpl.fieldAllowlist ?? null;
      set({
        baseCaps: caps,
        baseRowFilters: rows,
        baseFieldAllowlist: fields,
        ...(unchanged
          ? {
              effectiveCaps: caps,
              effectiveRowFilters: rows,
              effectiveRowFiltersWire: null,
              effectiveFieldAllowlist: fields,
            }
          : {}),
        ...patchedTargets(s, templateId, (t) => targetPatch(t, tmpl)),
      });
    }
    const after = get();
    const persisted = persistedFrom(after);
    if (persisted) writePersisted(persisted);
    if (after.eventId && templateId) {
      void queryClient.invalidateQueries({ queryKey: roleTemplatesKey(after.eventId) });
      if (tmpl) queryClient.setQueryData(rolePreviewScopeTemplateKey(after.eventId, templateId), tmpl);
    }
    scheduleReconcile();
  },

  reseedFromTemplate: (tmpl) => {
    const s = get();
    const templateId = s.target?.kind === "template" ? s.target.templateId : undefined;
    if (!savedTemplateOf(tmpl, templateId) || !templateId) return;
    const caps = tmpl.capabilities ?? [];
    const rows = tmpl.rowFilters ?? [];
    const fields = tmpl.fieldAllowlist ?? null;
    set({
      baseCaps: caps,
      effectiveCaps: caps,
      baseRowFilters: rows,
      effectiveRowFilters: rows,
      // Server rows are already wire-shaped.
      effectiveRowFiltersWire: null,
      baseFieldAllowlist: fields,
      effectiveFieldAllowlist: fields,
      ...patchedTargets(s, templateId, (t) => targetPatch(t, tmpl)),
    });
    const persisted = persistedFrom(get());
    if (persisted) writePersisted(persisted);
    scheduleReconcile();
  },

  patchTargetVersion: (templateId, version) => {
    set((s) => patchedTargets(s, templateId, (t) => ({ ...t, version })));
    const persisted = persistedFrom(get());
    if (persisted) writePersisted(persisted);
  },

  markScopeEdited: () => {
    // Slotting visibility lives on the template (resolved server-side per
    // request / WS upgrade), so no token re-mint is needed — but the open
    // slotting WS must reconnect to re-read the new scope, and REST must
    // refetch. Same in-place bust as the re-mint (no remount, no spinner),
    // plus the editor's scope template: exactly that saved state is new, and a
    // re-opened editor would show the old slotting state for 60 s otherwise.
    set((s) => ({ scopeRefreshNonce: s.scopeRefreshNonce + 1 }));
    const eventId = get().eventId;
    if (!eventId) return;
    void refreshEventScopedQueries(eventId);
    const key = rolePreviewScopeTemplateKey(eventId);
    void queryClient.cancelQueries(
      { queryKey: key, predicate: (q) => q.isActive() },
      { silent: true, revert: false },
    );
    void queryClient.invalidateQueries({ queryKey: key });
  },

  retryRemint: async (opts) => {
    // The EXPLICIT re-mint: the Retry button, the pause end, a failed switch,
    // the forced timer. Audit r2 (store LOW-1): while a forced re-mint is OWED
    // it is the forced one — the Retry button included (a plain one found the
    // state "already minted" and sent nothing while the live token could die
    // in 60 s). The debounce goes through `implicitRemint` (audit r3 MED-1).
    if (opts?.forced || forcedRemintOwed) return runForcedRemint();
    // Audit r4 (store LOW-1): the forced POST is in the air and the bar
    // offers the Retry again (its 20-s spinner cap is over) — join it, and pay
    // a debt it leaves with one more forced re-mint. Under the spinner there
    // is no Retry to press (a burst of calls there stays one POST).
    if (forcedFlight && forcedRun && !get().reminting) return retryAfterForcedFlight(forcedRun);
    await plainRemint();
  },

  startPreview: async (eventId, target, availableTargets) => {
    // Role SWITCH (already previewing): do NOT stopPreview first. That drops to
    // active:false and the board momentarily remounts as the admin → the slotting
    // WS upgrades under the admin cookie and streams the FULL unscoped board until
    // the new token lands (and PERSISTS if the new mint fails). Instead: mint the
    // new one under ADMIN auth (bypassPreview — the old read-only bearer can't
    // mint a different role), swap atomically (active stays true throughout),
    // then revoke the superseded token in the background.
    //
    // B2-0: every start/switch is a link of ONE chain (see `switchChain`).
    const enteredActive = get().active;
    pendingSwitches += 1;
    clearRemintTimer();
    // A forced re-mint due before the switch is not dropped: the switch's
    // commit replaces the token, its failure honours the debt.
    if (autoRemintTimer) {
      clearAutoRemintTimer();
      setForcedDebt(true);
    }
    const prev = switchChain;
    let release!: () => void;
    switchChain = new Promise<void>((r) => {
      release = r;
    });
    let turn: SwitchTurn = {};
    try {
      await prev; // links never reject
      turn = await runSwitchTurn(eventId, target, availableTargets, enteredActive);
    } finally {
      pendingSwitches -= 1;
      release();
    }
    if (turn.failedSwitch) afterFailedSwitch(turn.failedSwitch.noAnswer);
    if ("error" in turn) throw turn.error;
  },

  stopPreview: async () => {
    const { active, eventId, tokenId, isOwner } = get();
    if (!active) return;
    clearExpiryTimer();
    clearRemintTimer();
    clearAutoRemintTimer();
    clearPauseTimer();
    clearRemintingCap();
    setForcedDebt(false);
    failedForcedKey = null;
    rejectedMintKey = null;
    // A start/switch waiting for its turn or its mint is stale now.
    startSeq += 1;
    // R4-M1 (PB-FB-H3-PREV-STOPRACE): the stop's own session bump. The trailing
    // `eventId: null` below runs after an await — a NEW session committed in
    // that window must keep its eventId (else: banner "Preview as X" over the
    // admin view, no bearer, every write silently on the admin cookie).
    sessionEpoch += 1;
    const myEpoch = sessionEpoch;
    // Deactivate FIRST so the axios interceptor stops attaching the bearer and
    // stops blocking writes.
    set({
      active: false,
      token: null,
      tokenId: null,
      expiresAt: null,
      target: null,
      isOwner: false,
      startedHere: false,
      availableTargets: [],
      baseCaps: [],
      effectiveCaps: [],
      mintedReadCaps: [],
      baseRowFilters: [],
      effectiveRowFilters: [],
      effectiveRowFiltersWire: null,
      baseFieldAllowlist: null,
      effectiveFieldAllowlist: null,
      mintedScopeKey: "",
      reminting: false,
      remintError: false,
      remintErrorCode: null,
      remintPausedUntil: null,
      scopeUnsupported: false,
    });
    clearPersisted();
    if (eventId) bustEventScopedCaches(eventId);
    // Only the minting tab revokes — a hydrated builder tab tearing down must
    // not kill the session the main tab is still using. Non-owner sessions
    // expire server-side on their own (20 min TTL). Bypass: a new session may
    // start right after, and a replay of this DELETE would carry its bearer.
    if (isOwner && eventId && tokenId) {
      try {
        await revokeEventRoleApiToken(eventId, tokenId, true);
      } catch {
        // Best-effort: the token is short-lived (20 min) and auto-expires.
      }
    }
    if (sessionEpoch === myEpoch) set({ eventId: null });
  },
}));

/**
 * One start/switch at its TURN in the chain. Never throws: failures come back
 * as `{ error }` so the chain's `finally` (counter −1, release) always runs
 * BEFORE the failure follow-up, and the caller gets its own error.
 */
async function runSwitchTurn(
  eventId: string,
  target: PreviewTarget,
  availableTargets: PreviewTarget[] | undefined,
  enteredActive: boolean,
): Promise<SwitchTurn> {
  const store = usePreviewStore;
  try {
    // Read at the TURN (R4 LOW-4): a second Start queued behind a committed
    // first one runs as a switch of it (supersedes + revokes it), instead of a
    // second fresh session that orphans the first token.
    const wasActive = store.getState().active;
    // Stopped while queued: nothing to switch.
    if (enteredActive && !wasActive) return {};
    const mySeq = ++startSeq;
    // A running re-mint first — the switch then supersedes ITS token.
    while (remintPromise) await remintPromise;
    const stale = () => startSeq !== mySeq || (wasActive && !store.getState().active);
    // A stop in the wait costs no mint from the 20-bucket.
    if (stale()) return {};
    const atTurn = store.getState();
    const keptTargets = availableTargets ?? (wasActive ? atTurn.availableTargets : []);
    const supersedes =
      wasActive && atTurn.isOwner && atTurn.tokenId ? atTurn.tokenId : undefined;
    let res: PreviewTokenResponse;
    try {
      res = await createPreviewToken(
        eventId,
        {
          templateId: target.kind === "template" ? target.templateId : undefined,
          baseRole: target.kind === "baseRole" ? target.baseRole : undefined,
          ...(supersedes ? { supersedesTokenId: supersedes } : {}),
        },
        // Mint under the admin cookie: a no-op when not previewing (no bearer is
        // attached anyway), required on a switch. If this throws nothing below
        // runs, so the old session stays fully intact.
        true,
      );
    } catch (error) {
      const switching = wasActive && !stale();
      // A switch with a live predecessor draws on the re-mint bucket the edits
      // drain: its 429 pauses the running session's re-mints too, and the open
      // edit mints once at the pause end.
      if (switching && isRateLimited(error) && store.getState().target?.kind === "template") {
        startPause(retryAfterSeconds(error));
      }
      return switching
        ? { error, failedSwitch: { noAnswer: isNoAnswerOrGateway(error) } }
        : { error };
    }
    if (stale()) {
      // Stopped (or superseded) while minting: our fresh token is nobody's.
      revokeQuietly(eventId, res.eventRoleApiTokenId);
      return {};
    }
    const expiresAt = Date.parse(res.expiresAt);
    const normExpiry = Number.isNaN(expiresAt) ? null : expiresAt;
    // The token to revoke is the one live AT the swap (not one read before the
    // waits): exactly the one this mint superseded.
    const before = store.getState();
    const prevTokenId = before.active ? before.tokenId : null;
    const prevIsOwner = before.active && before.isOwner;
    // Audit r3 (security L1): a start from idle is THIS tab's session; a
    // switch keeps where the session started (a copy tab stays a copy tab).
    const startedHere = wasActive ? before.startedHere : true;
    const prevEventId = before.eventId;
    // Seed the editable cap + scope state from the new role (a role switch
    // re-seeds it, discarding any prior edits — you're now a different role).
    const seedCaps = target.capabilities;
    const seedRead = readOnlySubset(seedCaps);
    const seedRowFilters = target.rowFilters ?? [];
    const seedAllowlist = target.fieldAllowlist ?? null;
    sessionEpoch += 1;
    clearRemintTimer();
    clearAutoRemintTimer();
    clearPauseTimer();
    setForcedDebt(false);
    failedForcedKey = null;
    rejectedMintKey = null;
    // A new session of the ended event carries its own bearer (audit r1 F1).
    if (endedTombstone?.eventId === eventId) {
      endedTombstone = null;
      tombstoneEnded = null;
    }
    store.setState({
      active: true,
      eventId,
      token: res.token,
      tokenId: res.eventRoleApiTokenId,
      expiresAt: normExpiry,
      target,
      isOwner: true,
      startedHere,
      availableTargets: keptTargets,
      baseCaps: seedCaps,
      effectiveCaps: seedCaps,
      mintedReadCaps: seedRead,
      baseRowFilters: seedRowFilters,
      effectiveRowFilters: seedRowFilters,
      // Server-seeded rows are already wire-shaped.
      effectiveRowFiltersWire: null,
      baseFieldAllowlist: seedAllowlist,
      effectiveFieldAllowlist: seedAllowlist,
      // Freshly minted from templateId/baseRole → no scope override on the token.
      mintedScopeKey: "",
      reminting: false,
      remintError: false,
      remintErrorCode: null,
      remintPausedUntil: null,
      scopeUnsupported: false,
    });
    // Mirror to sessionStorage so new-tab builders inherit the read-only bearer.
    const persisted = persistedFrom(store.getState());
    if (persisted) writePersisted(persisted);
    // Refetch all event data THROUGH the preview bearer (scoped rows/fields).
    bustEventScopedCaches(eventId);
    armExpiry(normExpiry);
    // Role switch only: revoke the SUPERSEDED token now that the new one is live
    // (mirrors the re-mint). Only the owner revokes.
    if (wasActive && prevIsOwner && prevTokenId && prevEventId) {
      revokeQuietly(prevEventId, prevTokenId);
    }
    return {};
  } catch (error) {
    return { error };
  }
}

// Re-arm the expiry self-stop for a tab that hydrated an in-flight session from
// sessionStorage (the constructor can't reference get() cleanly).
if (hydrated) {
  armExpiry(hydrated.expiresAt);
}

/**
 * B2-0 (decision 3): end the preview LOCALLY — the current bearer is dead (401
 * `sessionExpired`) or the session's own end is near. Records `{eventId,
 * wasOwner, reason}` for `PreviewEndedNotice` and stops. A no-op while not
 * previewing (R3-FI3): a second call would overwrite `ended.eventId` with null
 * (the stop clears it for a non-owner synchronously) and the host would drop
 * the block.
 *
 * Audit r1 (security F1): an UNPLANNED end (`isUnplannedEnd`) first leaves the
 * request tombstone (see `PreviewTombstone`) — set BEFORE the stop flips
 * `active`, so no request of this event slips out on the cookie in between —
 * and blocks every tab of the event, the main tab included. Audit r2 (F2):
 * every end that BLOCKS (`endBlocks`) leaves it — a copy tab's expiry
 * self-stop too (it blocks until a reload; a write loop crossing it ran on the
 * cookie behind the notice). Only the main tab's planned expiry self-stop
 * leaves none: a toast, and it keeps working as the admin. Audit r3 (L1):
 * "main tab" is `startedHere`, not `isOwner` — a copy tab owns its token after
 * its own re-mint or switch and is still a copy tab.
 */
export function endPreviewLocally(reason: PreviewEndReason): void {
  const s = usePreviewStore.getState();
  if (!s.active) return;
  // Audit r3 (security L1): where the session STARTED, not who owns the live
  // token — a copy tab owns its token after its own re-mint or switch.
  const ended: PreviewEnded = { eventId: s.eventId, wasOwner: s.startedHere, reason };
  if (endBlocks(ended) && s.eventId) {
    endedTombstone = { eventId: s.eventId, tokenId: s.tokenId };
    tombstoneEnded = ended;
  }
  void s.stopPreview();
  usePreviewEndedStore.getState().end(ended);
}

/** The tombstone of a blocking local end in this tab, or null (audit r1 F1). */
export function getPreviewTombstone(): PreviewTombstone | null {
  return endedTombstone;
}

/**
 * Audit r2 (security F1): a request the tombstone refused raises the blocking
 * notice again when nothing blocking is shown — the notice's one slot may have
 * been replaced by another event's planned end and acknowledged by its host,
 * and back on this event every request was refused as "session expired" with
 * no notice (only a hard reload helped). Never replaces a record that blocks
 * (another host must stay blocked). Idempotent: the re-raised record blocks.
 */
export function reraisePreviewEnded(): void {
  if (!endedTombstone || !tombstoneEnded) return;
  const notice = usePreviewEndedStore.getState();
  if (notice.ended && endBlocks(notice.ended)) return;
  notice.end(tombstoneEnded);
}

/**
 * Plain (non-hook) snapshot for the axios interceptor, which runs outside React.
 * Returns the in-memory bearer ONLY when preview is active and the request is
 * scoped to the previewed event's path. `tokenId` + `sessionEpoch` (never the
 * secret) are stamped on the request config for the 401 rule.
 */
export function getPreviewAuth(): {
  active: boolean;
  eventId: string | null;
  token: string | null;
  tokenId: string | null;
  sessionEpoch: number;
} {
  const s = usePreviewStore.getState();
  return {
    active: s.active,
    eventId: s.eventId,
    token: s.token,
    tokenId: s.tokenId,
    sessionEpoch,
  };
}

/**
 * The previewed role's FULL (chrome) capability set as plain strings — the live
 * `effectiveCaps`, falling back to the target's real caps if a session hydrated
 * without an edit. Used by the axios response interceptor to tell a masked-write
 * 403 (cap IS in this set → the role can do it, the read-only token can't) from
 * a genuine permission gap (cap absent → leave it to the page's own UI).
 */
export function getPreviewCaps(): string[] {
  const s = usePreviewStore.getState();
  if (s.effectiveCaps.length > 0) return s.effectiveCaps as string[];
  return (s.target?.capabilities ?? []) as string[];
}

/** Test-only: drop every timer, counter and the session (module state included). */
export function __resetPreviewStoreForTests(): void {
  clearExpiryTimer();
  clearRemintTimer();
  clearAutoRemintTimer();
  clearPauseTimer();
  clearRemintingCap();
  remintInFlight = false;
  remintPromise = null;
  forcedRun = null;
  forcedFlight = null;
  forcedRetryAfter = null;
  forcedRemintOwed = false;
  failedForcedKey = null;
  rejectedMintKey = null;
  rejectedMintCode = null;
  rejectedMintSticky = false;
  switchChain = Promise.resolve();
  pendingSwitches = 0;
  endedTombstone = null;
  tombstoneEnded = null;
  // Anything still in flight from a previous test is stale from here on.
  startSeq += 1;
  sessionEpoch += 1;
  refreshGen += 1;
  usePreviewStore.setState({ ...IDLE_SESSION });
  clearPersisted();
}
